// src/screens/NuestrosClientesScreen.jsx
// Pantalla dentro de la PWA (con login) de "Nuestros Clientes": la vista
// pública de álbumes/reseñas + el formulario para que el cliente logueado
// deje su propia reseña. Toda reseña nace pendiente de moderación.
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/UI';
import { notifyAdmin } from '../services/authService';
import NuestrosClientesView from '../components/NuestrosClientesView';
import {
  enviarResena, enviarResenaConInvitacion, getGoogleReviewLinkPublico,
  getStoredResenaInvite, clearResenaInvite, invitacionResenaValida,
} from '../services/nuestrosClientesService';

// codigo: si viene un código de invitación válido (link personal que le
// mandó Yoedis), no hace falta pedir teléfono -- la invitación misma es
// la prueba de que es cliente real. Sin código, cualquier logueado puede
// opinar igual, pero el teléfono pasa a ser obligatorio.
const MAX_FOTOS_RESENA = 3;

function FormResena({ codigo, onClose, onEnviada }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [comentario, setComentario] = useState('');
  const [telefono, setTelefono] = useState('');
  const [fotos, setFotos] = useState([]); // File[]
  const [enviando, setEnviando] = useState(false);

  const previews = React.useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  React.useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const agregarFotos = (files) => {
    const nuevas = Array.from(files || []);
    setFotos((prev) => [...prev, ...nuevas].slice(0, MAX_FOTOS_RESENA));
  };
  const quitarFoto = (index) => setFotos((prev) => prev.filter((_, i) => i !== index));

  const enviar = async () => {
    if (!rating) return toast('Elige una cantidad de estrellas.', 'error');
    if (!codigo && !telefono.trim()) return toast('Escribe tu teléfono (así confirmamos que fuiste cliente).', 'error');
    setEnviando(true);
    try {
      if (codigo) {
        await enviarResenaConInvitacion({ codigo, rating, comentario, fotos });
        clearResenaInvite();
      } else {
        await enviarResena({ rating, comentario, telefono, fotos });
      }
      notifyAdmin(`⭐ Nueva reseña de ${user.name || 'un cliente'} (${rating}★) — pendiente de moderar`, '/?tab=more&admin=nuestros_clientes&sub=resenas');
      onEnviada(rating);
    } catch {
      toast('No se pudo enviar tu reseña. Intenta de nuevo.', 'error');
    }
    setEnviando(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 300, overflowY: 'auto' }}>
      <div style={{ padding: '24px 16px 40px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)' }}>Deja tu reseña</h2>
          <button onClick={onClose} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>✕</button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>¿Cómo calificarías tu experiencia?</p>
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} style={{ background: 'transparent', padding: 0, fontSize: 30, color: n <= rating ? 'var(--gold, #FFB800)' : 'var(--navy-border)' }}>★</button>
          ))}
        </div>

        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Tu opinión (opcional)</p>
        <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={5} placeholder="Cuéntanos cómo fue tu experiencia..." style={{
          width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12,
          padding: 12, color: 'var(--white)', fontSize: 13.5, marginBottom: 16, resize: 'vertical',
        }} />

        {!codigo && (
          <>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Tu teléfono (obligatorio, para verificar que eres cliente real — no se publica)</p>
            <input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="WhatsApp" style={{
              width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12,
              padding: 12, color: 'var(--white)', fontSize: 13.5, marginBottom: 24,
            }} />
          </>
        )}

        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Fotos (opcional, hasta {MAX_FOTOS_RESENA})</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {previews.map((url, i) => (
            <div key={i} style={{ position: 'relative', width: 72, height: 72 }}>
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
              <button onClick={() => quitarFoto(i)} style={{
                position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11,
                background: '#FF4455', color: 'white', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>
          ))}
          {fotos.length < MAX_FOTOS_RESENA && (
            <label style={{
              width: 72, height: 72, borderRadius: 10, border: '1px dashed var(--navy-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 24, cursor: 'pointer',
            }}>
              +
              <input type="file" accept="image/*" multiple hidden onChange={(e) => { agregarFotos(e.target.files); e.target.value = ''; }} />
            </label>
          )}
        </div>

        <button onClick={enviar} disabled={enviando} style={{
          width: '100%', background: 'var(--orange)', color: 'white', fontWeight: 800, fontSize: 14,
          borderRadius: 14, padding: '15px 0',
        }}>{enviando ? 'Enviando...' : 'Enviar reseña'}</button>
      </div>
    </div>
  );
}

function GraciasResena({ rating, onClose }) {
  const [googleLink, setGoogleLink] = useState(null);
  React.useEffect(() => { getGoogleReviewLinkPublico().then(setGoogleLink); }, []);
  const buena = rating >= 4;
  const mostrarGoogle = buena && googleLink;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', overflowY: 'auto' }}>
      <span style={{ fontSize: 44, marginBottom: 10 }}>🙏</span>
      <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', marginBottom: 6 }}>¡Gracias por tu opinión!</h2>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: mostrarGoogle ? 22 : 26, maxWidth: 320 }}>La recibimos y está en revisión.</p>

      {mostrarGoogle && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,107,0,0.16), rgba(255,184,0,0.10))',
          border: '1.5px solid rgba(255,184,0,0.5)', borderRadius: 20,
          padding: '24px 22px', marginBottom: 18, maxWidth: 340,
        }}>
          <p style={{ fontSize: 28, marginBottom: 6 }}>🥹🙌</p>
          <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)', marginBottom: 8, lineHeight: 1.3 }}>
            ¡Me hiciste el día! ¿Me ayudas con algo más?
          </p>
          <p style={{ fontSize: 13, color: 'var(--off-white, var(--white))', marginBottom: 18, lineHeight: 1.5 }}>
            Si de verdad te gustó el servicio, repetir esta misma opinión en Google me ayudaría muchísimo a seguir creciendo. Te toma 30 segundos 🙏
          </p>
          <a href={googleLink} target="_blank" rel="noopener noreferrer" style={{
            display: 'block', background: 'var(--orange)', color: 'white', fontWeight: 800, fontSize: 14.5,
            borderRadius: 14, padding: '16px 0', textDecoration: 'none', boxShadow: '0 6px 20px rgba(255,107,0,0.35)',
          }}>Sí, te ayudo en Google 🙌</a>
        </div>
      )}

      <button onClick={onClose} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 12, padding: '11px 20px', fontSize: 13 }}>
        Volver
      </button>
    </div>
  );
}

export default function NuestrosClientesScreen() {
  const [modo, setModo] = useState('vista'); // 'vista' | 'form' | 'gracias'
  const [ratingEnviado, setRatingEnviado] = useState(0);
  // Si llegó con un link de invitación válido, se lo pasamos al formulario
  // para que se salte el teléfono -- ver nota en FormResena.
  const [codigoValido, setCodigoValido] = useState(false);
  const codigo = getStoredResenaInvite();

  useEffect(() => { if (codigo) invitacionResenaValida(codigo).then(setCodigoValido); }, [codigo]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', paddingTop: 'env(safe-area-inset-top)' }}>
      <NuestrosClientesView canReview onLeaveReview={() => setModo('form')} />
      {modo === 'form' && (
        <FormResena codigo={codigoValido ? codigo : null} onClose={() => setModo('vista')} onEnviada={(rating) => { setRatingEnviado(rating); setModo('gracias'); }} />
      )}
      {modo === 'gracias' && <GraciasResena rating={ratingEnviado} onClose={() => { setCodigoValido(false); setModo('vista'); }} />}
    </div>
  );
}
